import { Link, useNavigate } from "react-router-dom";
import { Car, Key, Zap, ArrowRight, Eye, GraduationCap } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import OnboardingModal from "../components/OnboardingModal";

const HERO_BG =
  "https://images.unsplash.com/photo-1659947234309-804b7fa01cf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB0YXhpJTIwY2FtZXJvb258ZW58MHx8fHwxNzc1Mzc0ODc3fDA&ixlib=rb-4.1.0&q=85";

function ServicePill({ icon: Icon, label, to, testId }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur-sm border border-white/60 pl-1 pr-5 py-1 text-[#0A2540] hover:bg-white transition shadow-sm"
      data-testid={testId}
    >
      <span className="w-8 h-8 rounded-full bg-[#FACC15] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#0A2540]" strokeWidth={2.5} />
      </span>
      <span className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>
        {label}
      </span>
    </Link>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Auto-redirect logged-in users to their dashboard.
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") navigate("/admin", { replace: true });
    else if (user.role === "driver") navigate("/driver", { replace: true });
    else if (user.role === "partner") navigate("/partner", { replace: true });
    else navigate("/passenger", { replace: true });
  }, [user, navigate]);

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="landing-page"
      style={{ fontFamily: "Outfit, Inter, sans-serif" }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" loading="eager" />
        {/* Dark bottom → top gradient for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/90 via-[#0A2540]/60 to-[#0A2540]/25" />
      </div>

      {/* Content */}
      <div className="relative flex flex-col min-h-screen">
        {/* Spacer top */}
        <div className="flex-1" />

        {/* Bottom stack */}
        <div className="px-6 pb-10 sm:px-10 sm:pb-12 max-w-xl mx-auto w-full text-white">
          {/* Brand */}
          <div className="inline-flex items-center gap-2 bg-black/25 backdrop-blur-md rounded-full pl-1 pr-4 py-1 mb-6">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0 overflow-hidden">
              <img src="/trigo-logo.png" alt="TrigoTour" className="w-8 h-8 object-contain" />
            </div>
            <span className="text-base font-semibold tracking-tight">TrigoTour</span>
          </div>

          {/* Micro-label */}
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#FACC15] mb-3">
            Cameroun · Mobilité
          </p>

          {/* Headline */}
          <h1
            className="text-[42px] sm:text-5xl leading-[1.05] tracking-tight font-extrabold mb-5"
            style={{ fontFamily: "Outfit" }}
          >
            Voyagez ensemble,{" "}
            <span className="text-[#FACC15]">économisez plus.</span>
          </h1>

          {/* Description */}
          <p className="text-[15px] leading-relaxed text-white/85 mb-6 max-w-md">
            Covoiturage, location de véhicules avec chauffeur, et courses à la demande — partout au Cameroun.
          </p>

          {/* Service pills */}
          <div className="flex flex-wrap gap-2 mb-8">
            <ServicePill icon={Car} label="Covoiturage" to="/rides" testId="pill-covoiturage" />
            <ServicePill icon={Key} label="Location" to="/vehicles" testId="pill-location" />
            <ServicePill icon={Zap} label="Express" to="/express" testId="pill-express" />
          </div>

          {/* CTAs */}
          <div className="space-y-3">
            <Link
              to="/register"
              className="w-full inline-flex items-center justify-center h-14 rounded-full bg-[#FACC15] hover:bg-[#EAB308] transition text-[#0A2540] font-bold text-base shadow-lg shadow-[#FACC15]/20"
              data-testid="landing-cta-register"
            >
              Commencer
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link
              to="/login"
              className="w-full inline-flex items-center justify-center h-14 rounded-full border border-white/25 text-white/90 hover:bg-white/10 transition text-base"
              data-testid="landing-cta-login"
            >
              J&apos;ai déjà un compte
            </Link>

            {/* Secondary row : Explorer + Tutoriel */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Link
                to="/vehicles"
                className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 backdrop-blur-sm text-white text-sm font-semibold transition"
                data-testid="landing-cta-explorer"
              >
                <Eye className="w-4 h-4" /> Explorer
              </Link>
              <button
                type="button"
                onClick={() => setShowOnboarding(true)}
                className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 backdrop-blur-sm text-white text-sm font-semibold transition"
                data-testid="landing-cta-tutoriel"
              >
                <GraduationCap className="w-4 h-4" /> Tutoriel
              </button>
            </div>
          </div>

          {/* Bonus mention */}
          <p className="text-[12px] text-center text-white/70 mt-5">
            <span className="font-bold text-[#FACC15]">-15%</span> sur votre 1ère location · Valable 30 jours
          </p>
        </div>
      </div>

      <OnboardingModal open={showOnboarding} onClose={() => setShowOnboarding(false)} />
    </div>
  );
}
